import type { Fare, Hotel, PlanStop, Stay, Variant } from '@trippath/shared';

import { cents, type StopChange } from './stopInput';

/** A change to a variant's new place: as to a stop, and the way that leads there. */
export type PlaceChange = StopChange & {
  wayIn?: StopChange['way'];
  /** The hotels in mind there, the one stayed at first. */
  hotels?: Hotel[];
};

const none: Fare = { price: null, departureAt: null, arrivalAt: null, name: null, link: null };

/** Where a variant's new place `at` is in its route, as its nights, stays and fares count. */
function placeOf(variant: Variant, at: number): number {
  // The places before the first new one: the stop it leaves from, unless the trip was empty.
  return at + variant.codes.length - (variant.rejoin ? 1 : 0) - variant.added.length;
}

/**
 * A variant's new place's drafts — the hotel in mind, the ticket in mind for its way on — and
 * the stop whose way on leads there, with the ticket in mind for that.
 */
export function draftsOf(variant: Variant, at: number) {
  const place = placeOf(variant, at);
  const ticket = (leg: number) => {
    const fare = variant.fares[leg];
    return fare?.name ? { name: fare.name, link: fare.link } : null;
  };
  // Only the first new place says the way there: the others' is the one before's way on.
  const lead = at === 0 ? variant.into : null;
  return {
    into: lead ? { stop: lead, ticket: ticket(place - 1) } : null,
    drafts: { hotels: variant.stays[place - 1]?.hotels ?? [], ticket: ticket(place) },
  };
}

/**
 * What is said of a variant once its new place `at` changes: the nights there, its stay's
 * costs, the way there and the way on — each kept by its place in the route, the rest as said.
 */
export function saidWith(
  variant: Variant,
  at: number,
  stop: PlanStop,
  change: PlaceChange,
): { nights: number[]; fares: Fare[]; stays: (Stay | null)[] } {
  const place = placeOf(variant, at);
  const nights = [...variant.nights];
  const stays = [...variant.stays];
  const fares = [...variant.fares];
  const set = <T>(list: T[], index: number, value: T, empty: T) => {
    while (list.length < index) list.push(empty);
    list[index] = value;
  };
  if (place > 0 && change.days !== undefined) nights[place - 1] = change.days;
  const { accommodation, food, local, activities, hotels } = change;
  const changed = [accommodation, food, local, activities, hotels];
  if (place > 0 && changed.some((r) => r !== undefined)) {
    const was = stays[place - 1];
    set<Stay | null>(
      stays,
      place - 1,
      {
        accommodation: accommodation ?? cents(stop.accommodation),
        food: food ?? cents(stop.food),
        local: local ?? cents(stop.local),
        activities: activities ?? cents(stop.activities),
        hotels: hotels ?? (was ? was.hotels : []),
      },
      null,
    );
  }
  const fare = (way: NonNullable<StopChange['way']>, at: number): Fare => ({
    price: way.estimate,
    departureAt: way.departureAt,
    arrivalAt: way.arrivalAt,
    name: way.name === undefined ? (fares[at] ?? none).name : way.name,
    link: way.link,
  });
  if (place > 0 && change.wayIn) set(fares, place - 1, fare(change.wayIn, place - 1), none);
  if (change.way) set(fares, place, fare(change.way, place), none);
  return { nights, fares, stays };
}
